import React, { useState, useEffect } from 'react';

interface RangeSliderProps {
  values: [number, number];
  labels: Array<{ value: string; label: string }>;
  isPro?: boolean;
  onChange?: (values: [number, number]) => void;
}

const RangeSlider: React.FC<RangeSliderProps> = ({ values, labels, isPro = false, onChange }) => {
  const [lowValue, setLowValue] = useState(values[0]);
  const [highValue, setHighValue] = useState(values[1]);
  const [activeDragger, setActiveDragger] = useState<'low' | 'high' | null>(null);
  const sliderRef = React.useRef<HTMLDivElement>(null);
  const lowThumbRef = React.useRef<HTMLDivElement>(null);
  const highThumbRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLowValue(values[0]);
    setHighValue(values[1]);
    updatePositions(values[0], values[1]);
  }, [values]);

  const updatePositions = React.useCallback((low: number, high: number) => {
    if (!lowThumbRef.current || !highThumbRef.current) return;
    lowThumbRef.current.style.left = `${low}%`;
    highThumbRef.current.style.left = `${high}%`;
  }, []);

  const calculateValue = React.useCallback((clientX: number) => {
    if (!sliderRef.current) return 0;
    const rect = sliderRef.current.getBoundingClientRect();
    const position = clientX - rect.left;
    const percentage = (position / rect.width) * 100;
    return Math.min(Math.max(0, Math.round(percentage)), 100);
  }, []);

  const handleMove = React.useCallback((clientX: number) => {
    if (!activeDragger || !isPro) return;

    const newValue = calculateValue(clientX);
    
    if (activeDragger === 'low') {
      const constrainedValue = Math.min(newValue, highValue - 1);
      setLowValue(constrainedValue);
      updatePositions(constrainedValue, highValue);
      onChange?.([constrainedValue, highValue]);
    } else {
      const constrainedValue = Math.max(newValue, lowValue + 1);
      setHighValue(constrainedValue);
      updatePositions(lowValue, constrainedValue);
      onChange?.([lowValue, constrainedValue]);
    }
  }, [activeDragger, highValue, lowValue, onChange, calculateValue, isPro, updatePositions]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX);
    const handleMouseUp = () => setActiveDragger(null);

    if (activeDragger) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [activeDragger, handleMove]);

  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      handleMove(e.touches[0].clientX);
    };
    const handleTouchEnd = () => setActiveDragger(null);

    if (activeDragger) {
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [activeDragger, handleMove]);

  return (
    <div className="relative w-full h-32 pt-4 select-none">
      <div className="absolute top-6 left-0 right-0 h-6">
        <div 
          ref={sliderRef}
          className="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-3 rounded-lg"
          style={{
            background: `linear-gradient(to right, 
              #e5e7eb 0%,
              #e5e7eb ${lowValue}%,
              #60a5fa ${lowValue}%,
              #60a5fa ${highValue}%,
              #2563eb ${highValue}%,
              #2563eb 100%)`
          }}
        />

        <div
          ref={lowThumbRef}
          className={`absolute w-6 h-6 -ml-3 bg-white border-2 border-blue-600 rounded-full 
            cursor-grab active:cursor-grabbing hover:border-blue-500 hover:bg-blue-50 top-1/2 -translate-y-1/2
            ${activeDragger === 'low' ? 'z-20 scale-110' : 'z-10'} 
            ${!isPro ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ left: `${lowValue}%` }}
          onMouseDown={(e) => {
            e.preventDefault();
            if (isPro) {
              setActiveDragger('low');
            }
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            if (isPro) {
              setActiveDragger('low');
            }
          }}
        >
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-white rounded shadow-sm text-sm font-medium whitespace-nowrap pointer-events-none">
            {lowValue}点
          </div>
        </div>

        <div
          ref={highThumbRef}
          className={`absolute w-6 h-6 -ml-3 bg-white border-2 border-blue-600 rounded-full 
            cursor-grab active:cursor-grabbing hover:border-blue-500 hover:bg-blue-50 top-1/2 -translate-y-1/2
            ${activeDragger === 'high' ? 'z-20 scale-110' : 'z-10'} 
            ${!isPro ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ left: `${highValue}%` }}
          onMouseDown={(e) => {
            e.preventDefault();
            if (isPro) {
              setActiveDragger('high');
            }
          }}
          onTouchStart={(e) => {
            e.preventDefault();
            if (isPro) {
              setActiveDragger('high');
            }
          }}
        >
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-white rounded shadow-sm text-sm font-medium whitespace-nowrap pointer-events-none">
            {highValue}点
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 grid grid-cols-3 gap-2 text-center pointer-events-none">
        <div>
          <div className="text-sm font-medium">{labels[0].value}</div>
          <div className="text-xs text-gray-500 break-keep">{labels[0].label}（{lowValue}点未満）</div>
        </div>
        <div>
          <div className="text-sm font-medium">{labels[1].value}</div>
          <div className="text-xs text-gray-500 break-keep">{labels[1].label}（{lowValue}～{highValue}点）</div>
        </div>
        <div>
          <div className="text-sm font-medium">{labels[2].value}</div>
          <div className="text-xs text-gray-500 break-keep">{labels[2].label}（{highValue}点以上）</div>
        </div>
      </div>
    </div>
  );
};

export default RangeSlider;
